import { Policies, UserStatus } from "@fena/toolkit-types";
import { useQueryClient } from "@tanstack/react-query";
import { Auth } from "aws-amplify";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { AppRoutes } from "../../constants/routes";
import { useCompanyStore } from "../../stores/useCompanyStore";
import { useUserContext } from "../../context/UserContextProvider";
import { usePolicyCheck } from "../../modules/private/hooks/usePolicyCheck";
import {
  clearSelectedCompany,
  setSelectedCompanyMembership,
} from "../../util/company-utils";
import { FenaLogo, OrderGeneration2, SignOut } from "../icons";
import {
  Avatar,
  AvatarSizeVariant,
  Dropdown,
  Item,
  SettingsButton,
  SwitchButton,
  Typography,
  TypographyVariant,
} from "../ui-kit";
import { MenuItem } from "../ui-kit/avatar/Menu";
import { BurgerMenu } from "./burger-menu/BurgerMenu";
import { BurgerMenuItem } from "./burger-menu/BurgerMenuItem";
import { SubscriptionOptionEnum } from "../../modules/private/settings-module/billing-and-packages/shared/types";
import { usePartnerLayoutContext } from "../../context/PartnerLayoutContextProvider";

const Wrapper = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 3;
  background: #fff;
`;

const SubWrapper = styled.div`
  width: calc(100vw - 25px * 2);
  display: flex;
  align-items: center;
  height: 26px;
  box-shadow: 0 2px 18px rgba(108, 108, 138, 0.1);
  padding: 25px;
`;

const B2BWholesalerAppSubscriptionBanner = styled.div`
  border-radius: 0 0 5px 5px;
  background: rgba(196, 8, 193, 0.98);
  box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.25);
  padding: 5px 48px 5px 59px;
  height: calc(55px - 5px * 2);
  display: flex;
  justify-content: center;
  align-items: center;
`;

// const SearchBox = styled.div`
//   margin-left: 173px;
// `;

const RightContainer = styled.div`
  display: flex;
  flex-direction: row;
  margin-left: auto;
`;

const ProfileWrapper = styled.div`
  display: flex;
  gap: 10px;
  @media (max-width: 576px) {
    display: none;
  }
`;

const SignUpButtons = styled.div`
  display: flex;
`;

const SignInButton = styled(Typography)`
  margin-right: 15px;
  cursor: pointer;
`;
const SignUpButton = styled(Typography)`
  cursor: pointer;
`;

const SignOutIcon = styled(SignOut)`
  scale: 0.7;
`;

const StyledDropdown = styled(Dropdown)``;

const SwitchPartner = styled.div`
  display: flex;
  align-items: center;
`;

const SwitchLabel = styled(Typography)`
  margin-right: 10px;
`;

interface HeaderProps {
  landing?: boolean;
  customizationData?: any;
}

const Header: React.FunctionComponent<HeaderProps> = ({
  landing,
  customizationData,
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { clearUser, user, setUser } = useUserContext();
  const { company, clearCompany, parentCustomizationSettings } =
    useCompanyStore();
  const { isPartnerMode, handleSwitchToPartnerMode } =
    usePartnerLayoutContext();
  const [userMembershipsItems, setUserMembershipsItems] = useState<Item[]>([]);
  const [customization, setCustomization] = useState<any>(null);
  const subscriptionType =
    company?.currentSubscription?.details?.subscriptionOption ??
    company?.currentSubscription?.subscriptionOption;

  const StyledFenaLogo = styled(FenaLogo)`
    cursor: pointer;
    z-index: 3;
    @media (max-width: 576px) {
      pointer-events: none;
    }
  `;

  const StyledCustomLogo = styled.img`
    cursor: pointer;
    z-index: 3;
    height: 25px;
    object-fit: contain;
    @media (max-width: 576px) {
      pointer-events: none;
    }
  `;

  const renderLogo = () => {
    return parentCustomizationSettings?.partner_dashboard?.logo?.url ??
      customization?.customLogo ? (
      <StyledCustomLogo
        src={
          parentCustomizationSettings?.partner_dashboard?.logo?.url ??
          customization?.customLogo
        }
        alt="Company Logo"
      />
    ) : (
      <>
        <StyledFenaLogo />
      </>
    );
  };

  useEffect(() => {
    if (customizationData) {
      setCustomization(customizationData);
    }
  }, [customizationData]);

  useEffect(() => {
    if (!landing) {
      setUserMembershipsItems(
        user.companyMemberships
          ?.filter(
            (cm) => cm.deletedAt === null && cm.status === UserStatus.ACTIVE
          )
          ?.map((cm) => ({
            label: cm.company.tradingName,
            value: cm.company._id,
          }))
      );
    }
  }, [landing, user]);

  const { checkAccessByPolicies } = usePolicyCheck();
  const settingsNavigate = () => {
    if (isPartnerMode) {
      navigate(AppRoutes.Private.Partner.PARTNER_BUSINESS_ACCOUNT);
    } else {
      navigate(AppRoutes.Private.Settings.BUSINESS_ACCOUNT);
    }
  };

  const profileNavigate = () => {
    navigate(AppRoutes.Private.Profile.PROFILE_SETTINGS);
  };

  const handleLogOut = async () => {
    await Auth.signOut();
    clearUser();
    clearCompany();
    clearSelectedCompany();
    queryClient.clear();
    navigate(AppRoutes.Open.Auth.SIGN_IN_START);
  };

  const goToSignIn = () => {
    // navigate(AppRoutes.Open.Auth.SIGN_IN_START);
    navigate(AppRoutes.Open.Auth.CHECK_SIGN_IN);
  };

  const goToSignUp = () => {
    navigate(AppRoutes.Open.Auth.SIGN_UP_START);
  };

  // const goToDashboard = () => {
  //   navigate(AppRoutes.Private.Dashboard.DASHBOARD_MAIN);
  // };

  const goToOrders = () => {
    let destination;

    if (isPartnerMode) {
      destination = AppRoutes.Private.Partner.DASHBOARD;
    } else if (subscriptionType === SubscriptionOptionEnum.PAYMENTS_ONLY) {
      destination = AppRoutes.Private.Payments.TRANSACTIONS_LIST;
    } else if (company?.managedBy) {
      destination = AppRoutes.Private.Payments.TRANSACTIONS_LIST;
    } else {
      destination = AppRoutes.Private.Inventory.ORDERS_LIST;
    }

    navigate(destination);
  };

  const navigateToRoute =
    (routeName?: string) => (event: React.MouseEvent<HTMLElement>) => {
      navigate(routeName ?? "");
    };

  const handleSetSelectedCompany = (item: Item) => {
    clearSelectedCompany();
    setSelectedCompanyMembership(user.companyMemberships, item.value);
    setUser({
      ...user,
      selectedCompanyMembership: user.companyMemberships.find(
        (cm) => cm.company._id === item.value
      )!,
    });
    window.location.reload();
  };

  return (
    <Wrapper>
      {!!company?.b2bSubscription &&
        company?.b2bSubscription?.status === null && (
          <B2BWholesalerAppSubscriptionBanner>
            <Typography
              variant={TypographyVariant.HEADER2}
              color="#fff"
              style={{ textAlign: "center" }}
            >
              You haven&apos;t finalised selecting your Fena B2B Wholesaler App
              subscription on Shopify. Once you&apos;ve selected you
              subscription on Shopify, you will be able to access the Fena
              Toolkit fully.
            </Typography>
          </B2BWholesalerAppSubscriptionBanner>
        )}
      <SubWrapper>
        <div onClick={goToOrders}>{renderLogo()}</div>
        {/* <SearchBox> */}
        {/* <Search */}
        {/*  onChangeHandler={() => { */}
        {/*    console.warn(""); */}
        {/*  }} */}
        {/* /> */}
        {/* </SearchBox> */}
        <RightContainer>
          {landing ? (
            <SignUpButtons>
              <SignInButton
                onClick={goToSignIn}
                variant={TypographyVariant.BODY1}
              >
                Login
              </SignInButton>
              {window?.location?.href?.includes("fena.co") ? (
                <SignUpButton
                  onClick={goToSignUp}
                  variant={TypographyVariant.BODY1}
                >
                  Sign up
                </SignUpButton>
              ) : (
                <></>
              )}
            </SignUpButtons>
          ) : (
            <>
              <BurgerMenu>
                {checkAccessByPolicies(
                  [Policies.ORDER.ADMIN_ACCESS],
                  [Policies.ORDER.CREATE_ORDERS]
                ) ? (
                  <BurgerMenuItem
                    icon={<OrderGeneration2 />}
                    label="Order Generation"
                    isActive={window.location.pathname.includes(
                      "private/order-generation"
                    )}
                  >
                    <BurgerMenuItem
                      level={2}
                      label="In person"
                      isActive={window.location.pathname.includes(
                        AppRoutes.Private.Inventory.ORDER_GENERATION_EPOS
                      )}
                      onClick={navigateToRoute(
                        AppRoutes.Private.Inventory.ORDER_GENERATION_EPOS
                      )}
                    />
                    <BurgerMenuItem
                      level={2}
                      label="Delivery / pick-up"
                      isActive={window.location.pathname.includes(
                        AppRoutes.Private.Inventory
                          .ORDER_GENERATION_NEW_SALES_ORDER
                      )}
                      onClick={navigateToRoute(
                        AppRoutes.Private.Inventory
                          .ORDER_GENERATION_NEW_SALES_ORDER
                      )}
                    />
                  </BurgerMenuItem>
                ) : (
                  <></>
                )}
                {checkAccessByPolicies(
                  [Policies.WARHOUSE.ADMIN_ACCESS],
                  [Policies.WARHOUSE.VIEW_ORDER_TO_BE_PICKED]
                ) ? (
                  <BurgerMenuItem
                    label="Picking list"
                    isActive={window.location.pathname.includes(
                      AppRoutes.Private.Shipments.SHIPMENTS_PICKER_LIST
                    )}
                    onClick={navigateToRoute(
                      AppRoutes.Private.Shipments.SHIPMENTS_PICKER_LIST
                    )}
                  />
                ) : (
                  <></>
                )}
                <BurgerMenuItem
                  label="Bank accounts"
                  isActive={window.location.pathname.includes(
                    AppRoutes.Private.Settings.BANK_ACCOUNTS
                  )}
                  onClick={navigateToRoute(
                    AppRoutes.Private.Settings.BANK_ACCOUNTS
                  )}
                />
                <BurgerMenuItem
                  label="Sign-out"
                  icon={<SignOutIcon />}
                  lastItem
                  onClick={handleLogOut}
                />
              </BurgerMenu>

              <ProfileWrapper>
                {company?.canManageCompanies && (
                  <SwitchPartner>
                    <SwitchLabel
                      variant={TypographyVariant.BODY1}
                      color="#495b6c"
                    >
                      Partner mode
                    </SwitchLabel>
                    <SwitchButton
                      checked={isPartnerMode}
                      onChange={handleSwitchToPartnerMode}
                    />
                  </SwitchPartner>
                )}

                {user.companyMemberships.length > 1 && (
                  <StyledDropdown
                    items={userMembershipsItems}
                    onChange={handleSetSelectedCompany}
                    defaultItem={{
                      label:
                        user.selectedCompanyMembership?.company.tradingName ??
                        user.selectedCompanyMembership?.company.name ??
                        "",
                      value: user.selectedCompanyMembership?.company._id ?? "",
                    }}
                  />
                )}

                {checkAccessByPolicies(
                  [Policies.COMPANY.ADMIN_ACCESS],
                  [Policies.COMPANY.ACCESS_COMPANY_SETTINGS]
                ) && <SettingsButton onClick={settingsNavigate} />}
                <Avatar
                  size={AvatarSizeVariant.SMALL}
                  avatarSrc={user.avatar?.url}
                  isOnline
                >
                  <MenuItem onClick={profileNavigate}>Profile</MenuItem>
                  <MenuItem onClick={handleLogOut}>Log out</MenuItem>
                </Avatar>
              </ProfileWrapper>
            </>
          )}
        </RightContainer>
      </SubWrapper>
    </Wrapper>
  );
};

export default Header;
